import { useEffect, useMemo, useRef, useState } from 'react';

import { Stack } from '@mui/material';
import { useStore } from '@tanstack/react-form';
import { AxiosError } from 'axios';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router';
import { z } from 'zod';

import { ExpenseSummaryHeader } from '../components/ExpenseSummaryHeader';
import {
  expenseDetailsDefaultValues,
  ExpenseDetailsForm,
  expenseDetailsSchema,
  type ExpenseDetailsFormFields,
} from '../forms/ExpenseDetails';
import { ExpenseFileAttachments } from '../forms/ExpenseFileAttachments';
import {
  projectInformationDefaultValues,
  ProjectInformationForm,
  projectInformationSchema,
  type ProjectInformationFormFields,
} from '../forms/ProjectInformation';
import { useExpenseFormBlurredValuesStore } from '../stores/expenseFormBlurredValues';
import type { RegisterExpenseFormData } from '../types';
import {
  calculateExchangeRate,
  generatePersonalExpenseRedirectUrl,
  generateSpecialCaseErrorMessage,
  getFormIsLockedReasonKey,
} from '../utils';
import {
  useGetPersonalExpense,
  useUpdatePersonalExpense,
  useDeletePersonalExpense,
} from '@/api/REST/personalExpenses';
import { useGetUserMe } from '@/api/REST/userMe/useGetUserMe';
import { useGetCurrencyCodeOptionByIsoCode } from '@/api/typesense/currencyCodes';
import { PageTitle } from '@/components/global/PageTitle/PageTitle';
import { Panels } from '@/components/layouts/Panels';
import { DeleteButton } from '@/components/ui/DeleteButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { routes } from '@/constants/routes';
import { useAppForm } from '@/forms/useAppForm';
import { useFormatNumber } from '@/hooks/useFormatNumber';
import { useNotification } from '@/hooks/useNotification';

export const EditExpense = () => {
  const updatePersonalExpense = useUpdatePersonalExpense();
  const deletePersonalExpense = useDeletePersonalExpense();
  const { t } = useTranslation('expenses');
  const formRef = useRef<HTMLFormElement>(null);
  const { showError } = useNotification();
  const { id: expenseId } = useParams<{ id: string }>();

  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);

  const { reset: resetBlurredValueStore, setExpenseDetailValues } =
    useExpenseFormBlurredValuesStore();

  useEffect(() => {
    resetBlurredValueStore();

    // Cleanup function to reset when component unmounts
    return () => {
      resetBlurredValueStore();
    };
  }, []);

  const {
    data: expenseData,
    isLoading: isLoadingExpense,
    error: expenseDataError,
  } = useGetPersonalExpense(Number(expenseId));

  // stabilize parsed date value
  const parsedExpenseDate = useMemo(
    () =>
      !isLoadingExpense && expenseData ? parseISO(expenseData.date!) : null,
    [isLoadingExpense, expenseData],
  );

  const decimalFormattedInitialExpenseAmount = useFormatNumber(
    expenseData?.amount ?? -1,
  );

  const decimalFormattedInitialVatAmount = useFormatNumber(
    expenseData?.vatAmount ?? -1,
  );
  const initialTargetCurrencyConvertedAmount = expenseData
    ? expenseData?.amount * (expenseData?.exchangeRate / 100)
    : -1;

  const decimalFormattedInitialTargetCurrencyConvertedAmount = useFormatNumber(
    initialTargetCurrencyConvertedAmount,
  );

  const hasDataLoaded = !isLoadingExpense;

  const isProjectInactive = expenseData?.project?.isActive === false;
  const isContractInactive =
    expenseData?.projectSubContract?.isActive === false;

  const isExpenseLocked =
    hasDataLoaded &&
    (expenseData?.isApproved ||
      expenseData?.isInvoiced ||
      isProjectInactive ||
      isContractInactive);

  // Latch hasDataLoaded: once true it never reverts.
  // This prevents timing issues in refetches reported on background tabs (OIDC auth, TypeSense key rotation) from
  // temporarily flipping hasDataLoaded back to false, which causes defaultValues
  // to revert to empty and formApi.update() to wipe the form fields.
  const hasDataLoadedRef = useRef(false);
  if (hasDataLoaded) hasDataLoadedRef.current = true;
  const stableHasDataLoaded = hasDataLoadedRef.current;

  const form = useAppForm({
    defaultValues: {
      expense_details:
        stableHasDataLoaded && expenseData && parsedExpenseDate
          ? {
              date: parsedExpenseDate,
              supplier: expenseData.supplier ?? null,
              supplierInvoiceNumber: expenseData?.invoiceNo ?? '',
              paymentMethod: expenseData.paymentMethod ?? null,
              amountIncludingVat: decimalFormattedInitialExpenseAmount,
              vatAmount: decimalFormattedInitialVatAmount,
              expenseCurrency: expenseData.currency ?? null,
              expenseType: expenseData.expenseType ?? null,
              targetCurrencyConvertedAmount:
                decimalFormattedInitialTargetCurrencyConvertedAmount,
              expenseComment: expenseData?.comment ?? '',
              expenseNumber: expenseData?.expenseNo ?? '',
              billable: expenseData?.isBillable,
            }
          : expenseDetailsDefaultValues,
      project_information: (stableHasDataLoaded && expenseData
        ? {
            employee: expenseData.employee ?? null,
            customer: expenseData.customer ?? null,
            project: expenseData.project ?? null,
            contract: expenseData.projectSubContract ?? null,
          }
        : projectInformationDefaultValues) satisfies ProjectInformationFormFields,
    },
    validators: {
      onChange: z.object({
        expense_details: expenseDetailsSchema,
        project_information: projectInformationSchema,
      }),
    },
    onSubmit: async ({
      value,
    }: {
      value: {
        expense_details: ExpenseDetailsFormFields;
        project_information: ProjectInformationFormFields;
      };
    }) => {
      console.debug('Form submitted with values:', value);
      const payload = {
        expense_details: value.expense_details,
        project_information: value.project_information,
        // when targetCurrencyConvertedAmount is not set, we do not have a diverging currency, so exchange rate should calculate to 100
        exchangeRate: calculateExchangeRate(
          value.expense_details.amountIncludingVat,
          value.expense_details.targetCurrencyConvertedAmount
            ? value.expense_details.targetCurrencyConvertedAmount
            : value.expense_details.amountIncludingVat,
        ),
        legalEntityCurrencyIsoCode: selectedEmployeeCurrency
          ? selectedEmployeeCurrency.isoCode
          : '',
      } as RegisterExpenseFormData;

      if (!expenseData?.expenseId) {
        console.error('Expense id to update not loaded successfully');
        return;
      }

      await updatePersonalExpense.mutateAsync({
        id: expenseData?.expenseId,
        expenseData: payload,
      });
    },
    onSubmitInvalid: () => {
      // this error throwing is important to let SaveFormActions know that the submission failed
      throw new Error('Form is invalid');
    },
  });

  const handleDelete = () => async () => {
    if (!expenseData?.expenseId) {
      console.error('Expense id to delete not loaded successfully');
      return;
    }

    await deletePersonalExpense.mutateAsync(expenseData.expenseId);
  };

  // manually update string and date fields once the data has loaded as it is not always working setting them only as default values (maybe just a local issue?)
  useEffect(() => {
    if (
      expenseData?.vatAmount === undefined ||
      expenseData?.amount === undefined ||
      parsedExpenseDate === undefined ||
      !stableHasDataLoaded
    ) {
      return;
    }

    form.setFieldValue(
      'expense_details.amountIncludingVat',
      decimalFormattedInitialExpenseAmount,
    );
    //  we reset isTouched manually as the setting in setFieldValue for not updating meta is not working
    form.setFieldMeta('expense_details.amountIncludingVat', (prev) => ({
      ...prev,
      isTouched: false,
    }));
    // also set in the blurred values store to keep in sync
    setExpenseDetailValues({
      amountIncludingVat: decimalFormattedInitialExpenseAmount,
    });

    form.setFieldValue(
      'expense_details.vatAmount',
      decimalFormattedInitialVatAmount,
    );
    form.setFieldMeta('expense_details.vatAmount', (prev) => ({
      ...prev,
      isTouched: false,
    }));
    // also set in the blurred values store to keep in sync
    setExpenseDetailValues({
      vatAmount: decimalFormattedInitialVatAmount,
    });

    form.setFieldValue(
      'expense_details.targetCurrencyConvertedAmount',
      decimalFormattedInitialTargetCurrencyConvertedAmount,
    );
    form.setFieldMeta(
      'expense_details.targetCurrencyConvertedAmount',
      (prev) => ({
        ...prev,
        isTouched: false,
      }),
    );
    // also set in the blurred values store to keep in sync
    setExpenseDetailValues({
      targetCurrencyConvertedAmount:
        decimalFormattedInitialTargetCurrencyConvertedAmount,
    });

    if (parsedExpenseDate) {
      form.setFieldValue('expense_details.date', parsedExpenseDate);
      form.setFieldMeta('expense_details.date', (prev) => ({
        ...prev,
        isTouched: false,
      }));
    }
  }, [expenseData, parsedExpenseDate, stableHasDataLoaded]);

  // we need this to set the billable checkbox based on the default value of the selected contract
  const selectedContract = useStore(
    form.store,
    (state) => state.values.project_information.contract,
  );
  const previousSelectedContractId = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (selectedContract) {
      if (
        Number(selectedContract?.id) === expenseData?.projectSubContractId &&
        !previousSelectedContractId.current
      ) {
        previousSelectedContractId.current = Number(selectedContract.id);
        // this is the initial billable value of the expense, so we need to make sure billable is set on the expense value, not the contract default
        form.setFieldValue('expense_details.billable', expenseData?.isBillable);
        //  we reset isTouched manually as the setting in setFieldValue for not updating meta is not working
        form.setFieldMeta('expense_details.billable', (prev) => ({
          ...prev,
          isTouched: false,
        }));
      } else if (
        previousSelectedContractId.current === Number(selectedContract.id)
      ) {
        return; // no change in contract selection
      } else {
        form.setFieldValue(
          'expense_details.billable',
          selectedContract.isBillable,
        );
        previousSelectedContractId.current = Number(selectedContract.id);
      }
    }
  }, [selectedContract]);

  const selectedEmployee = useStore(
    form.store,
    (state) => state.values.project_information.employee,
  );

  const {
    data: selectedEmployeeCurrency,
    isLoading: isLoadingSelectedEmployeeCurrency,
  } = useGetCurrencyCodeOptionByIsoCode(
    selectedEmployee?.legalEntityCurrencyIsoCode ?? undefined,
  );

  const expenseIsUnchanged = useStore(
    form.store,
    (state) => state.isDefaultValue,
  );

  const { data: userMeData, isLoading: isUserMeLoading } = useGetUserMe();

  const successNavigationUrl = useMemo(() => {
    const params = {
      employeeUserId: form.state.values.project_information.employee?.id,
      projectId: form.state.values.project_information.project?.id,
      paymentMethodId: form.state.values.expense_details.paymentMethod?.id,
      date: form.state.values.expense_details.date,
    };
    return generatePersonalExpenseRedirectUrl(
      params,
      userMeData?.billingPlan.name === 'StarterPSA',
    );
  }, [form.state.values, userMeData]);

  const cancelNavigationUrl = useMemo(() => {
    const params = {
      employeeUserId: expenseData?.employeeUserId?.toString(),
      projectId: expenseData?.projectId?.toString(),
      paymentMethodId: expenseData?.paymentMethodId?.toString(),
      date: expenseData?.date ? expenseData.date : undefined,
    };
    return generatePersonalExpenseRedirectUrl(
      params,
      userMeData?.billingPlan.name === 'StarterPSA',
    );
  }, [expenseData, userMeData]);

  if (
    expenseDataError &&
    (expenseDataError as AxiosError).status === 404 &&
    !deletePersonalExpense.isSuccess
  ) {
    showError({
      message: t('edit.ExpenseNotFoundRedirectNotification'),
    });
    return <Navigate to={routes.register.expenses.new()} replace />;
  }

  const isSubmitDisabled =
    expenseIsUnchanged ||
    isExpenseLocked ||
    !hasDataLoaded ||
    isUploadingFiles ||
    isLoadingSelectedEmployeeCurrency ||
    isUserMeLoading;

  return (
    <>
      <PageTitle title={t('edit.PageTitle')} />
      <Panels autoSaveId="expense-panels">
        <Panels.Main panelContentProps={{ constrainWidth: true }}>
          <form.Subscribe selector={(state) => state.values}>
            {(val) => (
              <PageHeader
                title={t('edit.PageTitle')}
                subtitle={t('edit.PageSubtitle')}
              >
                <PageHeader.Info>
                  <ExpenseSummaryHeader
                    expenseCurrency={val.expense_details.expenseCurrency}
                    paymentMethod={val.expense_details.paymentMethod}
                    supplier={val.expense_details.supplier?.value}
                    category={val.expense_details.expenseType?.value}
                    selectedEmployee={val.project_information.employee}
                    expenseType="personal"
                  />
                </PageHeader.Info>
              </PageHeader>
            )}
          </form.Subscribe>
          <form.Form ref={formRef}>
            <Stack gap={{ xs: 2, sm: 4 }}>
              <ProjectInformationForm
                title={t('projectInformation.FormTitle')}
                fields="project_information"
                form={form}
                expenseType="personal"
                isParentLoading={!hasDataLoaded}
                formLocked={isExpenseLocked}
                lockedReason={getFormIsLockedReasonKey(
                  isProjectInactive,
                  isContractInactive,
                  !!expenseData?.isApproved,
                  !!expenseData?.isInvoiced,
                )}
              />

              <ExpenseDetailsForm
                title={t('expenseDetails.FormTitle')}
                fields="expense_details"
                form={form}
                expenseType="personal"
                expenseTargetCurrency={selectedEmployeeCurrency ?? null}
                isParentLoading={!hasDataLoaded}
                formLocked={isExpenseLocked}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <DeleteButton
                  disabled={isExpenseLocked || !hasDataLoaded}
                  deleteCallback={handleDelete()}
                  deletionRedirectPath={cancelNavigationUrl}
                />
                <form.SaveFormActions
                  form={form}
                  formRef={formRef}
                  data-automation-id="edit-expense-save-form-actions"
                  cancelNavigateToUrl={cancelNavigationUrl}
                  successNavigationUrl={successNavigationUrl}
                  onSubmit={form.handleSubmit}
                  isSubmitDisabled={isSubmitDisabled}
                  getErrorMessageFromError={generateSpecialCaseErrorMessage}
                />
              </Stack>
            </Stack>
          </form.Form>
        </Panels.Main>
        <Panels.Secondary panelContentProps={{ hideOverflow: true }}>
          <ExpenseFileAttachments
            expenseType="personal"
            expenseId={expenseData?.expenseId ?? null}
            isExpenseLocked={isExpenseLocked ?? false}
            isExpenseLoading={isLoadingExpense}
            onFilesUploading={setIsUploadingFiles}
          />
        </Panels.Secondary>
      </Panels>
    </>
  );
};
